import PropTypes from 'prop-types';
import { useState, useRef, useEffect, memo } from 'react';
// -*- mode: jsx;-*-
// Defined in general.js
/* global createCookie dcAnalytics toast setUpUserCookies showSignOutLink readCookie isEmailAddress
updateFavoritesCookie toggleSignInOverlay */

import { setup } from './followList.js';
import { Svg } from './Svg.js';
import { getURLParam } from '../adapters/dupes/queryParams.js';

export let FavoriteHeart = ({
    isFavorite, loadFavorites, trackingName,
    activeText, inactiveText, buttonText, additionalDescriptorText, additionalButtonClasses = '',
    favoriteType, favoriteValue, formDivClassName, pageURL,
}) => {
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [emailDisabled, setEmailDisabled] = useState(true);
    const [displayFavorite, setDisplayFavorite] = useState(isFavorite);
    const [message, setMessage] = useState('');
    const favoriteForm = useRef(null);

    // Stripping out chars that wouldn't be suitable for a function name
    //
    // This is written to prevent errors if favoriteType and favoriteValue are both undefined,
    // even though that should never be the case
    // I saw evidence in FullStory that someone had encountered it and it caused problems rendering search
    // I have not reproduced
    let callbackSuffix;
    if (favoriteType && favoriteValue) {
        callbackSuffix = (favoriteType + favoriteValue).replace(/\W/g, '');
    } else {
        callbackSuffix = 'MissingPropsSuffix';
    }

    const formName = 'followEmailForm' + callbackSuffix;
    const callbackName = 'followEmailCallback' + callbackSuffix;

    useEffect(() => {
        // We keep displayFavorite in local state so we can optimistically update it,
        // but we want to override it from props whenever applicable.
        setDisplayFavorite(isFavorite);
        // We include favoriteType & favoriteValue in the deps array because a change in either
        // one of those means the component represents a fundamentally different favorite record now.
        // Any optimistic change we made to displayFavorite would not apply to the new favorite record.
    }, [isFavorite, favoriteType, favoriteValue]);

    function followCallback(response) {
        const data = response.data || {};
        if (data.success) {
            // This cookie value is used as a cache buster in dc.common.followList.load
            createCookie('followDate', (new Date()).getTime(), 0);
            dcAnalytics.track('Follow Success', {
                trackingName, favoriteType, favoriteValue, pageName,
            });
            toast(data.message);
            setEmailDisabled(true);
            // We update the state of the heart because updates to the isFavorite prop require fully reloading
            // the user's favorites, and that can take a while.
            setDisplayFavorite(!displayFavorite);
        } else if (data.signinRequired) {
            // Register a handler to complete favoriting after signin
            // I think this will only work if the user signs in with a PW on the same page.
            // An email link will load in a separate tab, and therefore not trigger this.
            // I'm not sure it's worth trying to handle that, though.
            $(window).one('signinTriggered', () => {
                prepareFormSubmission();
                favoriteForm.current.submit();
            });
            toggleSignInOverlay(null, true, { user: { username: data.email } });
        } else if (data.emailRequired) {
            setShowEmailForm(true);
            setEmailDisabled(false);
            setMessage(data.message || 'Please sign in or provide a valid email');
        } else {
            toast(data.message || 'There was a problem adding your favorite. Please try again.');
        }

        setUpUserCookies();
        updateFavoritesCookie();
        showSignOutLink();
        setup(loadFavorites || 'loadFavorites');
    }

    function prepareFormSubmission() {
        dcAnalytics.track('Follow Clicked', {
            trackingName, favoriteType, favoriteValue, pageName,
        });

        // This will be called with the result of the JSONP form submission
        window[callbackName] = followCallback;

        // This call crosses domains, so we need to use iframes :(
        // (When this was originally written, it actually crossed from http to https. It's possible
        // we could use a lighter-weight alternative to an iframe now,
        // but I'm leaving the original code in place since it still works)
        const wiEl = document.getElementById('followIframe');
        if (wiEl) {
            // Remove existing iframe if applicable
            wiEl.parentNode.removeChild(wiEl);
        }
        const followIframe = document.createElement('iframe');
        followIframe.setAttribute('id', 'followIframe');
        followIframe.setAttribute('name', 'followIframe');
        followIframe.setAttribute('width', '0');
        followIframe.setAttribute('height', '0');
        followIframe.setAttribute('frameBorder', '0');
        document.body.appendChild(followIframe);
    }

    function toggleFavorite(event) {
        const userId = readCookie('userId');
        const email = favoriteForm.current.email.value;

        if (userId != null || isEmailAddress(email)) {
            prepareFormSubmission();
            favoriteForm.current.submit();
        } else {
            setShowEmailForm(true);
            setEmailDisabled(false);
        }

        event.preventDefault();
    }

    function closeEmailForm(event) {
        event.preventDefault();
        setShowEmailForm(false);
        setEmailDisabled(true);
    }

    function onSubmit() {
        setShowEmailForm(false);
        prepareFormSubmission();
    }

    const displayFormStyle = {
        display: showEmailForm ? 'inline-block' : 'none',
    };

    const updateURL = secureBaseURL + 'common/mmi_update.html';

    const id = getURLParam(pageURL, 'id');
    const verify = getURLParam(pageURL, 'verify');

    return (
        <span className="favorite-link-holder">
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <button tabIndex="0" role="button" aria-label="Follow for updates" className={'favorite-link ' + additionalButtonClasses + (displayFavorite ? ' saved' : '')} onClick={toggleFavorite}>
                <Svg icon="bookmark" attributes={{ role: 'presentation' }} />
                {displayFavorite ? activeText : inactiveText}
            </button>

            <div className="popup-overlay" style={displayFormStyle}></div>
            <div className={formDivClassName} style={displayFormStyle}>
                <div className="followEmail form-redux" style={displayFormStyle}>
                    <div className="email-favorite"></div>
                    <p>{additionalDescriptorText || 'We\'ll add this to your favorites and email you about relevant projects.'}</p>
                    <form name={formName} method="post" action={updateURL} onSubmit={onSubmit}
                        ref={favoriteForm} target="followIframe">
                        <input type="hidden" name="action" value="true"/>
                        <input type="hidden" name="callback" value={callbackName} />
                        <input type="hidden" name="postMessage" value="parent.postMessage"/>

                        <input type="hidden" name={favoriteType} value={favoriteValue}/>
                        <input type="hidden" name="remove" value={displayFavorite}/>

                        <input type="hidden" name="id" value={id}/>
                        <input type="hidden" name="verify" value={verify}/>

                        <div className="input-holder">
                            <label htmlFor="email">{message}</label>
                            <input type="email" name="email" placeholder="email address"
                                disabled={emailDisabled} autoComplete="off"/>
                        </div>
                        <input type="submit" className="button-secondary small" name="followSubmit"
                            value={buttonText}/>
                    </form>
                    <button tabIndex="0" onClick={closeEmailForm}>close</button>
                </div>
            </div>
        </span>
    );
};
FavoriteHeart.propTypes = {
    isFavorite: PropTypes.bool.isRequired,
    trackingName: PropTypes.string.isRequired,
    favoriteType: PropTypes.string.isRequired,
    favoriteValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    pageURL: PropTypes.string.isRequired,
    activeText: PropTypes.string.isRequired,
    inactiveText: PropTypes.string.isRequired,
    formDivClassName: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    additionalButtonClasses: PropTypes.string,
    additionalDescriptorText: PropTypes.string,
    loadFavorites: PropTypes.func,
    // If loadFavorites isn't passed, it has to be attached to window by the parent controller
};
FavoriteHeart = memo(FavoriteHeart);
